import { Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useContext } from "react";

import { AlignCenter, HTMLRenderer } from "../components";
import ConditionalWrapper from "../components/ConditionalWrapper";
import useCurrentSlideData from "../hooks/useCurrentSlideData";
import { SlideData } from "../interfaces/question.interface";
import { getRenderTemplateComponent } from "../util/questionUtils";
import { LangContext } from "../contexts/langContext";
import YesNoWrapper from "./YesNoWrapper";
import ConditionalFade from "./ConditionalFade";
import { Question } from "@common";
import { Navigate } from "react-router-dom";
import useIsMinimizedQuiz from "@natklok/hooks/useIsMinimizedQuiz";

const RenderQuestion: React.FC<{
  fadeInAnimation?: boolean;
  overrideableTitle?: string;
  overrideableDescription?: string;
  showCorrectAnswer?: boolean;
  question?: Question | undefined;
  readonly?: boolean;
  isPreview?: boolean;
}> = ({
  fadeInAnimation,
  overrideableTitle,
  overrideableDescription,
  showCorrectAnswer = false,
  question,
  readonly = false,
  isPreview = false,
}) => {
  const { translateTranslationData } = useContext(LangContext);
  const currentSlideData: SlideData = useCurrentSlideData();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const minimized = useIsMinimizedQuiz();

  if (!currentSlideData && !isPreview) {
    console.error("RenderQuestion not found");
    return <Navigate to="/question-not-found" />;
  }

  const currQuestion: Question | undefined =
    question !== undefined ? question : currentSlideData.question;
  if (currQuestion === undefined) return null;

  const title = overrideableTitle
    ? overrideableTitle
    : translateTranslationData(currQuestion.title);
  const desc = overrideableDescription
    ? overrideableDescription
    : translateTranslationData(currQuestion.desc);

  let contentFadeDelay = (0.5 + title.length / 40) * 1000;
  if (currQuestion.renderTemplate === "test-password-strength") {
    contentFadeDelay = 0;
  }

  return (
    <div style={{ transform: minimized ? "scale(0.9)" : undefined }}>
      <ConditionalWrapper
        condition={currQuestion.renderTemplate === "yes-no"}
        wrapper={(children: any) => (
          <YesNoWrapper
            readonly={readonly}
            showCorrect={showCorrectAnswer}
            contentFadeDelay={fadeInAnimation ? contentFadeDelay : 0}
            questionRenderProps={currQuestion.renderProps}
          >
            {children}
          </YesNoWrapper>
        )}
      >
        <AlignCenter
          style={{ height: currQuestion.renderTemplate === "yes-no" ? undefined : "80vh" }}
          withMaxWidth={!minimized && true}
          centerBothAxis={currQuestion.renderTemplate !== "yes-no"}
        >
          <ConditionalFade
            fadeInAnimation={fadeInAnimation}
            contentFadeDelay={0}
            currQuestion={currQuestion}
          >
            <div
              style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}
            >
              <Typography
                variant="h4"
                fontSize={isMobile ? 20 : undefined}
                component="h1"
                mt={isMobile ? 0 : 1}
                mb={1}
              >
                {title}
              </Typography>
            </div>
          </ConditionalFade>
          <ConditionalFade
            fadeInAnimation={fadeInAnimation}
            contentFadeDelay={contentFadeDelay}
            currQuestion={currQuestion}
          >
            <>
              {overrideableDescription !== "" && (
                <Typography
                  variant="h5"
                  component="h5"
                  marginBottom={isMobile ? 1.5 : 3}
                  fontWeight={500}
                >
                  <HTMLRenderer>{desc}</HTMLRenderer>
                </Typography>
              )}
            </>
          </ConditionalFade>
          <ConditionalFade
            fadeInAnimation={fadeInAnimation}
            contentFadeDelay={contentFadeDelay}
            currQuestion={currQuestion}
          >
            <div>{getRenderTemplateComponent(currQuestion, readonly, showCorrectAnswer)}</div>
          </ConditionalFade>
        </AlignCenter>
      </ConditionalWrapper>
    </div>
  );
};

export default RenderQuestion;
